import React, { useEffect, useState } from "react";
import { DndContext, closestCenter, DragEndEvent } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  Chip,
  Select,
  MenuItem,
} from "@mui/material";
import {
  getAllMenuPageList,
  getPagelistdropdowndata,
  menudeleteApi,
  MenusEditApi,
  MenusEditOrderApi,
} from "services/apiService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faFileCirclePlus,
  faFileExport,
  faFloppyDisk,
  faMagnifyingGlass,
  faPenToSquare,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import toast from "react-hot-toast";
import MenuMap from "./MenuMap";

const DraggableRow = ({
  setData,
  item,
  index,
  isEditing,
  onEditClick,
  onSaveClick,
  onChange,
  pagesList,
  onActionComplete,
}: {
  setData: React.Dispatch<React.SetStateAction<any[]>>;
  item;
  index: number;
  isEditing: boolean;
  onEditClick: () => void;
  onSaveClick: () => void;
  onChange: (field, value: any) => void;
  pagesList: any;
  onActionComplete: () => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.menu_id });

  const handleDelete = async (id: string) => {
    try {
      const mapnumber = Number(id);
      const response = await menudeleteApi(mapnumber);
      if (response.status_code === 200) {
        toast.success("Menu deleted successfully");
        setData((prevData) => prevData.filter((item) => item.menu_id !== id));
        onActionComplete();
      }
    } catch (error) {
      console.log(error);
      toast.error("Error deleting menu");
    }
  };

  return (
    <TableRow
      ref={setNodeRef}
      sx={{
        backgroundColor: "white",
        cursor: "default",
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <TableCell align="center" style={{ padding: "1px" }}>
        <Button
          variant="contained"
          {...listeners}
          {...attributes}
          sx={{
            backgroundColor: "#1976d2",
            color: "white",
            "&:hover": { backgroundColor: "#115293" },
            cursor: "grab !important",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "20px ",
            borderRadius: "0px",
          }}
        >
          <FontAwesomeIcon icon={faBars} />
        </Button>
      </TableCell>
      <TableCell align="center">{index + 1}</TableCell>
      <TableCell align="center">{item.menu_name}</TableCell>
      <TableCell align="center">
        {isEditing ? (
          <div>
            <div>
              <Select
                value={item.page_id}
                onChange={(e) => onChange("page_id", e.target.value)}
              >
                {pagesList.map((page) => (
                  <MenuItem key={page.page_id} value={page.page_id}>
                    {page.page_name}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </div>
        ) : (
          <span style={{ color: "blue" }}>{item.page_name}</span>
        )}
      </TableCell>
      <TableCell align="center">
        <Chip
          label={item.is_active ? "Active" : "Inactive"}
          sx={{
            backgroundColor: item.is_active ? "#e0f7e9" : "#f8d7da",
            color: item.is_active ? "green" : "red",
            fontWeight: "bold",
          }}
        />
      </TableCell>
      <TableCell align="center">
        {isEditing ? (
          <Button onClick={onSaveClick} sx={{ minWidth: "40px", mx: 1 }}>
            <FontAwesomeIcon icon={faFloppyDisk} style={{ fontSize: "1rem" }} />
          </Button>
        ) : (
          <Button onClick={onEditClick} sx={{ minWidth: "40px", mx: 1 }}>
            <FontAwesomeIcon
              icon={faPenToSquare}
              style={{ fontSize: "1rem" }}
            />
          </Button>
        )}

        <Button
          onClick={() => handleDelete(item.map_id)}
          sx={{ minWidth: "40px", mx: 1 }}
        >
          <FontAwesomeIcon icon={faTrash} style={{ fontSize: "1rem" }} />
        </Button>
      </TableCell>
    </TableRow>
  );
};

const MenuBoard = () => {
  const [data, setData] = useState([]);
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [pagesList, setPagesList] = useState<any>([]);
  const [isOrderChanged, setIsOrderChanged] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    fetchMenuList();
    fetchPagesList();
  }, []);

  const fetchPagesList = async () => {
    try {
      const response = await getPagelistdropdowndata();
      if (response.status_code === 200) {
        setPagesList(response.data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchMenuList = async () => {
    try {
      const response = await getAllMenuPageList();
      if (response.status_code === 200) {
        setData(response.data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = data.findIndex((item) => item.menu_id === active.id);
    const newIndex = data.findIndex((item) => item.menu_id === over.id);
    const newData = [...data];
    const [movedItem] = newData.splice(oldIndex, 1);
    newData.splice(newIndex, 0, movedItem);
    setData(newData);
    setIsOrderChanged(true);
  };

  const handleEditClick = (id: string) => {
    setEditingRow(id);
  };

  const handleSaveClick = async ({ id, page_id, menu_id }) => {
    const payload = {
      id: id,
      page_id: page_id,
      menu_id: menu_id,
    };
    try {
      const response = await MenusEditApi(payload);
      if (response.status_code === 200) {
        toast.success("Menus updated succefully");
        fetchMenuList();
      }
    } catch (error) {
      toast.error("menu updation failed");
    }
    setEditingRow(null);
  };

  const handleChange = (id: string, field, value: any) => {
    setData((prevData) =>
      prevData.map((item) =>
        item.menu_id === id ? { ...item, [field]: value } : item
      )
    );
  };

  const handleSaveOrder = async () => {
    const payload = {
      menu_place: data.map((item, index) => ({
        map_id: item.map_id,
        place_order: index + 1,
      })),
    };

    try {
      const response = await MenusEditOrderApi(payload);
      if (response.status_code === 200) {
        toast.success("Menus Order updated successfully!");
        setIsOrderChanged(false);
        fetchMenuList();
      }
    } catch (error) {
      toast.error("Menus Order updation failed!");
    }
  };

  const handleModelClose = async () => {
    setIsOpen(false);
  };

  return (
    <>
      <div className="d-flex justify-content-between p-2">
        <div className="col-lg-6">
          <Button
            variant="outlined"
            color="secondary"
            sx={{ borderRadius: "0px" }}
            onClick={() => setIsOpen(true)}
          >
            <FontAwesomeIcon
              icon={faFileCirclePlus}
              style={{ marginRight: "10px" }}
            />
            Map menus
          </Button>
        </div>
        <div className="col-lg-5 d-flex mb-3">
          <input
            type="text"
            className="form-control"
            style={{ boxShadow: "none" }}
            placeholder="Search Menus..."
          />
          <div className="input-group-append">
            <span className="input-group-text">
              <FontAwesomeIcon icon={faMagnifyingGlass} size="xl" />
            </span>
          </div>
        </div>
      </div>
      <Box sx={{ margin: "auto", textAlign: "center", padding: 1 }}>
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={data.map((item) => item.menu_id)}
            strategy={verticalListSortingStrategy}
          >
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f4f4f4" }}>
                    <TableCell></TableCell>
                    <TableCell align="center">
                      <strong>S.no</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Menus</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Mapped Pages</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Status</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Action</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((item, index) => (
                    <DraggableRow
                      setData={setData}
                      key={item.menu_id}
                      onActionComplete={fetchMenuList}
                      item={item}
                      index={index}
                      isEditing={editingRow === item.menu_id}
                      onEditClick={() => handleEditClick(item.menu_id)}
                      onSaveClick={() =>
                        handleSaveClick({
                          id: item.map_id,
                          page_id: item.page_id,
                          menu_id: item.menu_id,
                        })
                      }
                      onChange={(field, value) =>
                        handleChange(item.menu_id, field, value)
                      }
                      pagesList={pagesList}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </SortableContext>
        </DndContext>
      </Box>
      {isOrderChanged && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", padding: 2 }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleSaveOrder}
          >
            <FontAwesomeIcon
              icon={faFileExport}
              style={{ marginRight: "10px" }}
            />
            Save Order Changes
          </Button>
        </Box>
      )}

      <MenuMap
        open={isOpen}
        handleClose={handleModelClose}
        onActionComplete={() => {
          fetchMenuList();
        }}
      />
    </>
  );
};

export default MenuBoard;
